/**
 * Written pages for the best-known elements, keyed by symbol: an intro plus sections
 * ({ heading, paragraphs }) on where it is found, what we use it for, how it was discovered
 * and three fun facts. Shown on the element page above the reaction lists. An element page is
 * indexed once its slug is also listed in indexableElementPages.js.
 */
export const ELEMENT_ARTICLES = Object.freeze({
  H: {
    intro: 'Hydrogen is the simplest and lightest element of all. Each atom has just one proton in its centre and one electron around it. It is a colourless gas with no smell, and it burns very easily.',
    sections: [
      {
        heading: 'Where is hydrogen found?',
        paragraphs: [
          "Hydrogen is the most common element in the universe. Stars like our Sun are mostly hydrogen, and so are giant planets like Jupiter. On Earth, pure hydrogen gas is rare, because it is so light that it escapes into space. Instead, most of Earth's hydrogen is locked up in water, and in the molecules of every living thing.",
        ],
      },
      {
        heading: 'What do we use hydrogen for?',
        paragraphs: [
          'Factories use huge amounts of hydrogen to make ammonia for fertiliser and to clean up fuels in oil refineries. Liquid hydrogen is a powerful rocket fuel. Hydrogen fuel cells can power buses, cars and even trains, making electricity and leaving only water behind.',
        ],
      },
      {
        heading: 'How hydrogen was discovered',
        paragraphs: [
          'In 1766 the English scientist Henry Cavendish showed that the gas made when metals react with acids was a substance of its own. He called it inflammable air. A few years later the French chemist Antoine Lavoisier named it hydrogen, meaning water former, because it makes water when it burns.',
        ],
      },
      {
        heading: 'Three fun facts about hydrogen',
        paragraphs: [
          'About six out of every ten atoms in your body are hydrogen atoms, mostly in the water inside you.',
          'Every second, the Sun turns roughly 600 million tonnes of hydrogen into helium. That is what makes it shine.',
          'Hydrogen is the only element whose most common atoms have no neutrons at all.',
        ],
      },
    ],
  },
  He: {
    intro: 'Helium is a light, colourless gas that never burns and almost never reacts with anything. It is a noble gas, and it is the gas that makes party balloons float.',
    sections: [
      {
        heading: 'Where is helium found?',
        paragraphs: [
          'Helium is the second most common element in the universe, made in huge amounts inside stars. On Earth it is rare in the air, because it is so light that it slowly drifts off into space. The helium we use is collected from natural gas deep underground, where it has built up over millions of years as radioactive elements in rocks slowly break down.',
        ],
      },
      {
        heading: 'What do we use helium for?',
        paragraphs: [
          'Besides floating balloons and airships, liquid helium is used to cool the powerful magnets in hospital MRI scanners to about minus 269 °C. Divers breathe helium mixed with oxygen on very deep dives, and welders use helium to shield hot metal from the air.',
        ],
      },
      {
        heading: 'How helium was discovered',
        paragraphs: [
          'Helium was found in the Sun before it was found on Earth. During a solar eclipse in 1868, astronomers including Pierre Janssen and Norman Lockyer saw a bright yellow line in sunlight that did not match any known element. It was named after Helios, the Greek god of the Sun. In 1895 William Ramsay finally found helium on Earth, inside a uranium mineral.',
        ],
      },
      {
        heading: 'Three fun facts about helium',
        paragraphs: [
          'Helium is the only element that will not freeze at normal pressure, however cold it gets.',
          'Helium makes voices sound squeaky because sound travels faster through it. Breathing it in from balloons is not safe, though, because it pushes oxygen out of your lungs.',
          'Once helium escapes into the air, it is very hard to get back, so scientists worry about wasting it.',
        ],
      },
    ],
  },
  Li: {
    intro: 'Lithium is the lightest metal of all. It is soft enough to cut with a knife and so light that it floats on water, though it reacts with the water as it does.',
    sections: [
      {
        heading: 'Where is lithium found?',
        paragraphs: [
          "Lithium is never found as a pure metal in nature, because it reacts too easily. It is mined from rocks such as spodumene, especially in Australia, and from very salty water pumped up from under salt flats in South America. Chile, Argentina and Bolivia hold some of the world's biggest lithium supplies.",
        ],
      },
      {
        heading: 'What do we use lithium for?',
        paragraphs: [
          'Most lithium today goes into rechargeable batteries for phones, laptops, power tools and electric cars. Lithium is also used in heat-resistant glass and ceramics, in long-lasting greases for machines, and in medicines that help some people with their mood. Lithium compounds make a bright crimson red in fireworks.',
        ],
      },
      {
        heading: 'How lithium was discovered',
        paragraphs: [
          'In 1817 the Swedish chemist Johan August Arfwedson was studying a mineral called petalite from a mine on the island of Utö. He realised it contained a new element. Because it was discovered in a rock, it was named after the Greek word lithos, meaning stone. A year later, scientists used electricity to make the first tiny sample of the metal.',
        ],
      },
      {
        heading: 'Three fun facts about lithium',
        paragraphs: [
          'Lithium is so light that it would float on oil as well as on water.',
          'When lithium compounds are put in a flame, they turn it a deep crimson red.',
          'Lithium was one of only three elements made in the Big Bang, along with hydrogen and helium.',
        ],
      },
    ],
  },
  C: {
    intro: 'Carbon is the element of life. Its atoms can link to each other in long chains and rings, so carbon can build millions of different compounds, from sugar and plastic to the DNA inside you.',
    sections: [
      {
        heading: 'Where is carbon found?',
        paragraphs: [
          "Carbon is in every living thing, and about one-fifth of your body's weight is carbon. It is in the air as carbon dioxide, in rocks such as limestone and chalk, and in coal, oil and natural gas, which formed from ancient plants and sea creatures. Pure carbon is found as soft grey graphite and as hard, sparkling diamond.",
        ],
      },
      {
        heading: 'What do we use carbon for?',
        paragraphs: [
          'Carbon fuels such as coal, petrol and gas have powered homes and machines for centuries. A little carbon turns iron into steel. Graphite is the grey core of pencils, diamonds cut through hard stone, and carbon fibre makes light, strong bikes, tennis rackets and aeroplane parts.',
        ],
      },
      {
        heading: 'How carbon was discovered',
        paragraphs: [
          'People have used carbon as charcoal and soot since prehistoric times, so nobody discovered it. In 1772 Antoine Lavoisier burned a diamond and showed it made carbon dioxide. In 1796 Smithson Tennant proved that diamond is pure carbon, just like charcoal. Lavoisier listed carbon as an element in his famous table of 1789.',
        ],
      },
      {
        heading: 'Three fun facts about carbon',
        paragraphs: [
          'Diamond and pencil graphite are both pure carbon. The only difference is how the atoms are arranged.',
          'Scientists can tell the age of old bones and wood by measuring a rare type of carbon called carbon-14.',
          'Graphene, a sheet of carbon just one atom thick, is one of the strongest materials ever measured.',
        ],
      },
    ],
  },
  N: {
    intro: 'Nitrogen is a colourless gas with no smell that makes up most of the air you breathe. It is so unreactive that it passes in and out of your lungs without changing.',
    sections: [
      {
        heading: 'Where is nitrogen found?',
        paragraphs: [
          "Nitrogen makes up about 78 percent of Earth's air. It is also in every living thing, because proteins and DNA contain nitrogen atoms. In soil, nitrogen is found in compounds called nitrates, which plants soak up through their roots.",
        ],
      },
      {
        heading: 'What do we use nitrogen for?',
        paragraphs: [
          "The biggest use of nitrogen is making ammonia for fertiliser, which helps grow much of the world's food. Liquid nitrogen, at minus 196 °C, is used to freeze food quickly, store cells in hospitals and make dramatic clouds in science shows. Nitrogen gas fills crisp packets to keep them fresh, and many aircraft tyres are filled with it.",
        ],
      },
      {
        heading: 'How nitrogen was discovered',
        paragraphs: [
          'In 1772 Daniel Rutherford, a student in Edinburgh, Scotland, studied the air left after something had burned in a sealed jar. A candle would not burn in it and a mouse could not live in it. He called it noxious air. Later the French chemist Jean-Antoine Chaptal named it nitrogène, meaning maker of nitre, a nitrogen compound used in gunpowder.',
        ],
      },
      {
        heading: 'Three fun facts about nitrogen',
        paragraphs: [
          'Tiny bacteria living in lumps on the roots of peas and beans can turn nitrogen gas into plant food.',
          'Lightning joins nitrogen and oxygen together, and the rain carries the result into the soil.',
          'Liquid nitrogen is so cold that a flower dipped in it becomes brittle enough to shatter like glass.',
        ],
      },
    ],
  },
  O: {
    intro: 'Oxygen is the gas that keeps you alive. Every breath you take brings oxygen into your lungs, where your blood picks it up and carries it to every cell in your body.',
    sections: [
      {
        heading: 'Where is oxygen found?',
        paragraphs: [
          "Oxygen makes up about 21 percent of the air. It is the most common element in Earth's crust, because rocks and sand are full of oxygen compounds. Water is mostly oxygen by weight, and about two-thirds of your body's weight is oxygen. Plants, algae and tiny ocean plankton make the oxygen in the air using sunlight.",
        ],
      },
      {
        heading: 'What do we use oxygen for?',
        paragraphs: [
          'Hospitals give pure oxygen to patients who need help breathing. Steelworks blow oxygen through melted iron to burn away extra carbon. Welders use oxygen to make very hot flames, and rockets carry liquid oxygen so their fuel can burn in space, where there is no air.',
        ],
      },
      {
        heading: 'How oxygen was discovered',
        paragraphs: [
          'Oxygen was discovered by two people separately. The Swedish chemist Carl Wilhelm Scheele made it in the early 1770s, and the English scientist Joseph Priestley made it in 1774 by heating a red mercury compound. Antoine Lavoisier named it oxygen and showed that burning needs it.',
        ],
      },
      {
        heading: 'Three fun facts about oxygen',
        paragraphs: [
          'Liquid oxygen is pale blue and is attracted to a magnet.',
          'A special form of oxygen called ozone forms a layer high in the sky that blocks harmful rays from the Sun.',
          'Tiny plankton floating in the oceans make a large share of the oxygen we breathe.',
        ],
      },
    ],
  },
  F: {
    intro: 'Fluorine is a pale yellow gas and the most reactive element of all. It grabs electrons from almost anything it touches, so it is far too dangerous to handle outside special labs.',
    sections: [
      {
        heading: 'Where is fluorine found?',
        paragraphs: [
          'Fluorine is never found as a free gas in nature, because it reacts with everything. It is locked inside minerals, especially fluorite, which forms beautiful purple, green and yellow crystals. Small amounts of fluoride are also found in sea water, soil and some drinking water.',
        ],
      },
      {
        heading: 'What do we use fluorine for?',
        paragraphs: [
          'You probably meet fluorine every morning in fluoride toothpaste, which helps protect your teeth from decay. Fluorine is used to make the non-stick coating on frying pans, gases for fridges and air conditioners, and many modern medicines. It is also used to make the uranium compound needed for nuclear fuel.',
        ],
      },
      {
        heading: 'How fluorine was discovered',
        paragraphs: [
          'Fluorine was one of the hardest elements to separate, and several chemists were badly hurt trying. In 1886 the French chemist Henri Moissan finally made it by passing electricity through a cold liquid in special equipment. He won the Nobel Prize in Chemistry in 1906. The name comes from fluorite, from the Latin word fluere, to flow.',
        ],
      },
      {
        heading: 'Three fun facts about fluorine',
        paragraphs: [
          'Fluorine can make even water, glass and some noble gases react.',
          'Some fluorite crystals glow under ultraviolet light, and the word fluorescence was named after them.',
          'Pure fluorine has to be stored in special metal containers, because it attacks glass.',
        ],
      },
    ],
  },
  Ne: {
    intro: 'Neon is a colourless noble gas that glows a brilliant red-orange when electricity passes through it. That glow made neon famous in shop signs around the world.',
    sections: [
      {
        heading: 'Where is neon found?',
        paragraphs: [
          'Neon is the fifth most common element in the universe, but on Earth it is rare. Only a tiny fraction of the air is neon, about 18 parts in every million. It is collected by cooling air until it turns into a liquid and then separating the different gases as they warm up.',
        ],
      },
      {
        heading: 'What do we use neon for?',
        paragraphs: [
          'Neon is used in glowing advertising signs, indicator lights and some high-voltage warning lamps. Helium-neon lasers make a red beam that was once used in barcode scanners. Liquid neon is also used to cool things to very low temperatures.',
        ],
      },
      {
        heading: 'How neon was discovered',
        paragraphs: [
          'In 1898 the British chemists William Ramsay and Morris Travers cooled air into a liquid and carefully let it boil, collecting gases one by one. When they put a new gas into a glass tube and passed electricity through it, it glowed a blazing crimson. They named it neon, from the Greek word neos, meaning new.',
        ],
      },
      {
        heading: 'Three fun facts about neon',
        paragraphs: [
          'Only red-orange signs are truly neon. Other colours come from different gases or coloured coatings on the glass.',
          'The first neon sign was shown in Paris in 1910 by the French inventor Georges Claude.',
          'Neon does not form any stable compounds with other elements.',
        ],
      },
    ],
  },
  Na: {
    intro: 'Sodium is a soft, silvery metal so reactive that it must be stored under oil. You will never see the pure metal at home, but you eat its compounds every day.',
    sections: [
      {
        heading: 'Where is sodium found?',
        paragraphs: [
          "Sodium is the sixth most common element in Earth's crust, but it is always joined to other elements. The ocean is full of dissolved sodium chloride, which makes sea water salty. Thick layers of rock salt lie underground where ancient seas dried up long ago.",
        ],
      },
      {
        heading: 'What do we use sodium for?',
        paragraphs: [
          'Sodium chloride is table salt, used to flavour and preserve food and to melt ice on roads. Sodium hydrogen carbonate is baking soda, which helps cakes rise. Sodium hydroxide is used to make soap and paper. Liquid sodium metal carries heat in some nuclear reactors, and hot sodium vapour once lit many streets with a yellow-orange glow.',
        ],
      },
      {
        heading: 'How sodium was discovered',
        paragraphs: [
          'In 1807 Humphry Davy, working at the Royal Institution in London, passed a strong electric current through caustic soda. Little beads of a shiny, very reactive metal appeared. He named it sodium after soda. Its symbol, Na, comes from natrium, an old name for soda.',
        ],
      },
      {
        heading: 'Three fun facts about sodium',
        paragraphs: [
          'Sodium is soft enough to cut with a butter knife.',
          'Sodium makes flames glow bright yellow, and it gives fireworks their yellow colour.',
          'Your nerves need sodium to send messages around your body.',
        ],
      },
    ],
  },
  Mg: {
    intro: 'Magnesium is a light, silvery metal that burns with a dazzling white light. It is also hidden inside every green leaf, where it helps plants catch sunlight.',
    sections: [
      {
        heading: 'Where is magnesium found?',
        paragraphs: [
          "Magnesium is one of the most common elements in Earth's crust, found in minerals such as dolomite and magnesite. Sea water contains a lot of dissolved magnesium, and some magnesium metal is made from it. Magnesium is also in nuts, seeds, beans and leafy green vegetables.",
        ],
      },
      {
        heading: 'What do we use magnesium for?',
        paragraphs: [
          'Magnesium is about two-thirds as heavy as aluminium, so it is mixed with other metals to make light parts for cars, bikes, laptops and cameras. It makes the bright white sparks in fireworks and flares. Magnesium compounds are used in indigestion medicines and in Epsom salts for bath soaks.',
        ],
      },
      {
        heading: 'How magnesium was discovered',
        paragraphs: [
          'Magnesium is named after Magnesia, a part of ancient Greece where magnesium minerals were found. In 1755 the Scottish chemist Joseph Black showed that magnesia was different from lime. In 1808 Humphry Davy used electricity to make a tiny amount of the metal, and in 1831 Antoine Bussy made larger amounts.',
        ],
      },
      {
        heading: 'Three fun facts about magnesium',
        paragraphs: [
          'Every molecule of chlorophyll, the green chemical in plants, has a magnesium atom at its centre.',
          'Burning magnesium cannot be put out with water, which makes the fire worse.',
          'Your body uses magnesium to help your muscles and nerves work.',
        ],
      },
    ],
  },
  Al: {
    intro: 'Aluminium is a light, silvery metal that never rusts. It is so useful that you probably touch something made of aluminium every day, from drink cans to kitchen foil.',
    sections: [
      {
        heading: 'Where is aluminium found?',
        paragraphs: [
          "Aluminium is the most common metal in Earth's crust. It is found in clay and many rocks, but it holds onto oxygen so tightly that it is never found as a pure metal. Most aluminium is made from an ore called bauxite, mined in places such as Australia, Guinea and China.",
        ],
      },
      {
        heading: 'What do we use aluminium for?',
        paragraphs: [
          'Aluminium is used for drink cans, foil, window frames, bike frames, cooking pans and the bodies of aeroplanes, trains and some cars. It carries electricity well and is light, so it is used in long-distance power cables. Phone and laptop cases are often made of aluminium too.',
        ],
      },
      {
        heading: 'How aluminium was discovered',
        paragraphs: [
          'In 1825 the Danish scientist Hans Christian Ørsted made a small lump of impure aluminium, and in 1827 Friedrich Wöhler in Germany made purer samples. For decades it was more precious than silver. In 1886 Charles Martin Hall in America and Paul Héroult in France separately found a way to make it cheaply using electricity, a method still used today.',
        ],
      },
      {
        heading: 'Three fun facts about aluminium',
        paragraphs: [
          'Recycling an aluminium can uses only about one-twentieth of the energy needed to make a new one from ore.',
          'Rubies and sapphires are crystals of aluminium oxide.',
          'In America it is spelled and pronounced aluminum.',
        ],
      },
    ],
  },
  Si: {
    intro: 'Silicon is a shiny, grey, hard element that is part metal and part non-metal. It is the material at the heart of almost every computer chip in the world.',
    sections: [
      {
        heading: 'Where is silicon found?',
        paragraphs: [
          "Silicon is the second most common element in Earth's crust, after oxygen. Together they make silica, the main mineral in sand and quartz. Most rocks and clays are built from silicon and oxygen combined with metals. Pure silicon is never found in nature and has to be made in electric furnaces.",
        ],
      },
      {
        heading: 'What do we use silicon for?',
        paragraphs: [
          'Ultra-pure silicon is sliced into thin wafers to make computer chips and the solar cells in solar panels. Sand, which is mostly silica, is melted to make glass. Silicon compounds are in concrete and bricks, and silicones, which are rubbery materials made with silicon, are used in bakeware, sealants and phone cases.',
        ],
      },
      {
        heading: 'How silicon was discovered',
        paragraphs: [
          'In 1824 the Swedish chemist Jöns Jacob Berzelius made fairly pure silicon for the first time by heating a silicon compound with potassium. The name comes from the Latin word silex, meaning flint, a hard stone made of silica.',
        ],
      },
      {
        heading: 'Three fun facts about silicon',
        paragraphs: [
          'Silicon Valley in California, home of many computer companies, is named after silicon.',
          'Silicon for computer chips must be about 99.9999999 percent pure.',
          'Tiny sea creatures called diatoms build delicate glass shells out of silica.',
        ],
      },
    ],
  },
  P: {
    intro: 'Phosphorus is a non-metal that comes in several forms. White phosphorus glows in the dark and catches fire by itself, while red phosphorus is much safer and is used on matchboxes.',
    sections: [
      {
        heading: 'Where is phosphorus found?',
        paragraphs: [
          'Phosphorus is never found pure in nature. It is mined as phosphate rock, a mineral found in large amounts in Morocco, China and the United States. Phosphorus is also in every living thing. Your bones and teeth are built from calcium phosphate, and every strand of your DNA has phosphorus in it.',
        ],
      },
      {
        heading: 'What do we use phosphorus for?',
        paragraphs: [
          'Most phosphorus is used to make fertilisers, because plants need it to grow strong roots and flowers. It is used on the striking strip of matchboxes, in some cleaning products, in fireworks, and in food as an ingredient that helps baking and gives some fizzy drinks their tang.',
        ],
      },
      {
        heading: 'How phosphorus was discovered',
        paragraphs: [
          'Phosphorus was discovered in 1669 by Hennig Brand, a German alchemist who was hoping to find a way to make gold. He boiled down large amounts of urine and heated what was left. Instead of gold, he got a white substance that glowed eerily in the dark. It was named after the Greek word for light bringer.',
        ],
      },
      {
        heading: 'Three fun facts about phosphorus',
        paragraphs: [
          'Phosphorus was the first element whose discoverer is known by name.',
          "An adult's body contains more than half a kilogram of phosphorus, mostly in the bones.",
          'The glow of white phosphorus comes from a slow reaction with oxygen in the air.',
        ],
      },
    ],
  },
  S: {
    intro: 'Sulfur is a bright yellow, brittle non-metal. It has been known since ancient times, when it was called brimstone, meaning burning stone.',
    sections: [
      {
        heading: 'Where is sulfur found?',
        paragraphs: [
          'Sulfur is often found as yellow crystals around volcanoes and hot springs, and in deep underground deposits. Many minerals contain sulfur, such as pyrite and gypsum. Most sulfur used today is removed from oil and natural gas when they are cleaned. Sulfur is also part of some of the proteins in your hair, skin and nails.',
        ],
      },
      {
        heading: 'What do we use sulfur for?',
        paragraphs: [
          'Most sulfur is turned into sulfuric acid, one of the most important chemicals in industry, used to make fertilisers, batteries and many other products. Sulfur is used to make rubber tough, to protect grapes and other crops from mould, and in matches and fireworks.',
        ],
      },
      {
        heading: 'How sulfur was discovered',
        paragraphs: [
          'People have known about sulfur for thousands of years. It was burned to clean homes and is mentioned in many ancient writings. In 1777 the French chemist Antoine Lavoisier convinced other scientists that sulfur is an element rather than a compound. In 1839 Charles Goodyear discovered that heating rubber with sulfur made it strong and stretchy.',
        ],
      },
      {
        heading: 'Three fun facts about sulfur',
        paragraphs: [
          'Pure sulfur has almost no smell. The rotten egg smell comes from a sulfur gas, hydrogen sulfide.',
          "Jupiter's moon Io is covered in volcanoes that spray out sulfur, giving it yellow and orange colours.",
          'Sulfur burns with a blue flame.',
        ],
      },
    ],
  },
  Cl: {
    intro: 'Chlorine is a pale green gas with a sharp smell. It is poisonous as a gas, but joined with sodium it becomes ordinary table salt.',
    sections: [
      {
        heading: 'Where is chlorine found?',
        paragraphs: [
          'Chlorine is too reactive to be found free in nature. Most of it is in sodium chloride, dissolved in the sea or buried as rock salt. Every litre of sea water holds about 19 grams of chloride. Chlorine is made by passing electricity through salty water.',
        ],
      },
      {
        heading: 'What do we use chlorine for?',
        paragraphs: [
          'Small amounts of chlorine keep drinking water and swimming pools free of germs, and have saved millions of lives. Chlorine is used to make PVC, a plastic used for pipes, window frames and raincoats. It is in household bleach and is used to make many medicines and other chemicals.',
        ],
      },
      {
        heading: 'How chlorine was discovered',
        paragraphs: [
          'In 1774 the Swedish chemist Carl Wilhelm Scheele made a greenish-yellow gas by mixing hydrochloric acid with a black mineral. He thought it was a compound containing oxygen. In 1810 Humphry Davy proved it was an element and named it from the Greek word chloros, meaning pale green.',
        ],
      },
      {
        heading: 'Three fun facts about chlorine',
        paragraphs: [
          'Your stomach makes hydrochloric acid, which contains chlorine, to help digest food.',
          'Chlorine gas is about two and a half times heavier than air, so it sinks.',
          'The smell of a swimming pool mostly comes from chlorine reacting with sweat, not from chlorine itself.',
        ],
      },
    ],
  },
  Ar: {
    intro: 'Argon is a colourless, odourless noble gas that hardly reacts with anything. Its name comes from the Greek word for lazy.',
    sections: [
      {
        heading: 'Where is argon found?',
        paragraphs: [
          "Argon makes up almost one percent of the air, which makes it the third most common gas in the air after nitrogen and oxygen. Most of it was made deep inside Earth's rocks over billions of years, as a radioactive form of potassium slowly broke down. It is collected by cooling air until it turns into a liquid.",
        ],
      },
      {
        heading: 'What do we use argon for?',
        paragraphs: [
          'Because argon does not react, it is used to protect things. Old-style light bulbs were filled with argon so the glowing wire would not burn up. Welders use argon to shield hot metal from the air. It fills the gap in some double-glazed windows to keep homes warm, and it protects precious old documents from slowly decaying.',
        ],
      },
      {
        heading: 'How argon was discovered',
        paragraphs: [
          'In 1894 the British scientists Lord Rayleigh and William Ramsay noticed that nitrogen taken from air was slightly heavier than nitrogen made from chemicals. They removed every known gas from a sample of air and found a small amount of a new gas left over. It was the first noble gas to be discovered.',
        ],
      },
      {
        heading: 'Three fun facts about argon',
        paragraphs: [
          'There is more argon in the air than carbon dioxide, more than 20 times as much.',
          'The original copy of the US Declaration of Independence is kept in a case filled with argon.',
          'Argon glows a pale violet-blue when electricity passes through it.',
        ],
      },
    ],
  },
  K: {
    intro: 'Potassium is a soft, silvery metal that reacts so strongly with water that it bursts into a lilac flame. In its safe, combined forms, it is something your body needs every day.',
    sections: [
      {
        heading: 'Where is potassium found?',
        paragraphs: [
          "Potassium is common in Earth's crust, but always joined to other elements in minerals. Large underground beds of potassium salts, called potash, formed where ancient seas dried up, and are mined in Canada, Russia and Belarus. Potassium is also dissolved in sea water and found in plants, especially bananas, potatoes, beans and leafy greens.",
        ],
      },
      {
        heading: 'What do we use potassium for?',
        paragraphs: [
          'About nine-tenths of the potash mined is used as fertiliser, because plants need potassium to grow and stay healthy. Potassium compounds are also used in soft soaps, some glass, salt substitutes for people who must eat less sodium, and in fireworks and matches.',
        ],
      },
      {
        heading: 'How potassium was discovered',
        paragraphs: [
          'Potassium was the first metal ever separated using electricity. In 1807 Humphry Davy passed a strong current through potash and saw tiny shining beads of metal appear and burst into flame. He named the element after potash, which got its name because it was made by soaking wood ashes in pots. Its symbol, K, comes from kalium, its Latin name.',
        ],
      },
      {
        heading: 'Three fun facts about potassium',
        paragraphs: [
          'Your heart needs potassium to keep a steady beat.',
          'Bananas are very slightly radioactive because a tiny share of their potassium atoms are a radioactive kind, but it is completely harmless.',
          'Potassium makes flames turn a pale lilac colour.',
        ],
      },
    ],
  },
  Ca: {
    intro: 'Calcium is a silvery metal that you never see on its own, because it reacts with water and air. Joined with other elements, it builds bones, teeth, shells and whole mountains.',
    sections: [
      {
        heading: 'Where is calcium found?',
        paragraphs: [
          "Calcium is the fifth most common element in Earth's crust. It is in limestone, chalk and marble, which are all forms of calcium carbonate, and in gypsum, used for plaster. Many sea creatures build their shells from calcium carbonate, and when they die their shells pile up to form new rock. Milk, cheese, yoghurt, nuts and green vegetables contain calcium too.",
        ],
      },
      {
        heading: 'What do we use calcium for?',
        paragraphs: [
          'Limestone heated in kilns makes lime, which is used to make cement and concrete for buildings, roads and bridges. Gypsum is made into plasterboard for walls. Calcium compounds are used to make chalk, toothpaste, antacid tablets and some medicines, and calcium metal helps remove unwanted elements when making steel.',
        ],
      },
      {
        heading: 'How calcium was discovered',
        paragraphs: [
          'People have used lime and plaster for thousands of years, but calcium metal is very hard to separate. In 1808 Humphry Davy used electricity to make a small amount of it from a mixture of lime and mercury oxide. He named it after calx, the Latin word for lime.',
        ],
      },
      {
        heading: 'Three fun facts about calcium',
        paragraphs: [
          'Calcium is the most common metal in your body. An adult has about a kilogram of it, almost all in bones and teeth.',
          'The White Cliffs of Dover are made of chalk, built from the shells of tiny sea creatures.',
          'Stalactites and stalagmites in caves are made of calcium carbonate, left behind by dripping water.',
        ],
      },
    ],
  },
  Ti: {
    intro: 'Titanium is a strong, light, silvery metal that does not rust. It is as strong as many steels but much lighter, which makes it perfect for aircraft, spacecraft and even parts of the human body.',
    sections: [
      {
        heading: 'Where is titanium found?',
        paragraphs: [
          "Titanium is the ninth most common element in Earth's crust, but it is always locked inside minerals, mainly ilmenite and rutile. These are often found as dark grains in beach sands, in places such as Australia, South Africa and Canada. Getting the pure metal out is difficult and expensive.",
        ],
      },
      {
        heading: 'What do we use titanium for?',
        paragraphs: [
          'Titanium is used in jet engines, aircraft frames and spacecraft. Because the body does not reject it, it is used for hip and knee replacements, dental implants and screws that hold broken bones together. It also makes light bikes, golf clubs and glasses frames. Most titanium is turned into titanium dioxide, a brilliant white powder used in paint, paper, sunscreen and toothpaste.',
        ],
      },
      {
        heading: 'How titanium was discovered',
        paragraphs: [
          'In 1791 William Gregor, a vicar in Cornwall, England, who loved studying minerals, found an unknown metal in black sand from a valley near his home. A few years later the German chemist Martin Heinrich Klaproth found the same element in another mineral and named it after the Titans, powerful giants of Greek myths.',
        ],
      },
      {
        heading: 'Three fun facts about titanium',
        paragraphs: [
          'Bone can grow right onto titanium, which helps implants stay firmly in place.',
          'The Guggenheim Museum in Bilbao, Spain, is covered in thin, shimmering titanium panels.',
          'Titanium burns brightly in pure nitrogen, one of the few metals that can.',
        ],
      },
    ],
  },
  Fe: {
    intro: 'Iron is a strong grey metal and the most used metal in the world. Mixed with a little carbon it becomes steel, and it is also the element that makes your blood red.',
    sections: [
      {
        heading: 'Where is iron found?',
        paragraphs: [
          "Iron is the fourth most common element in Earth's crust, and Earth's core is mostly iron and nickel. It is mined from rusty-red ores such as haematite and black magnetic ones such as magnetite, especially in Australia, Brazil and China. Iron is also in foods like red meat, beans, spinach and fortified cereals.",
        ],
      },
      {
        heading: 'What do we use iron for?',
        paragraphs: [
          'Almost all iron is made into steel for buildings, bridges, cars, ships, trains, tools and household goods. Cast iron, which has more carbon, is used for heavy pans and old-style drain covers. Iron compounds are also used in some medicines and as a red-brown colouring in paint and make-up.',
        ],
      },
      {
        heading: 'How iron was discovered',
        paragraphs: [
          'Iron has been used for thousands of years. The earliest iron objects were made from metal that fell from space in meteorites. A dagger buried with the Egyptian pharaoh Tutankhamun has a blade made of meteorite iron. Around 3,000 years ago people learned to get iron from rock in hot furnaces, starting what is called the Iron Age.',
        ],
      },
      {
        heading: 'Three fun facts about iron',
        paragraphs: [
          'Your blood contains a protein called haemoglobin, with iron at its heart, that carries oxygen around your body.',
          'Mars looks red because its surface dust is rich in iron oxide, a kind of rust.',
          'A natural magnetic iron rock called lodestone was used to make some of the very first compasses.',
        ],
      },
    ],
  },
  Cu: {
    intro: 'Copper is a soft, orange-pink metal that carries electricity and heat extremely well. It was one of the very first metals people learned to use.',
    sections: [
      {
        heading: 'Where is copper found?',
        paragraphs: [
          'Copper is sometimes found as pure lumps of metal in rocks, but most is mined from ores such as chalcopyrite, which contains copper, iron and sulfur. Chile and Peru are the biggest producers. Copper is also in your body in tiny amounts and in foods such as nuts, seeds and shellfish.',
        ],
      },
      {
        heading: 'What do we use copper for?',
        paragraphs: [
          'Most copper is made into electrical wires, cables and motors, because only silver carries electricity better. It is used for water pipes, roofing, cooking pans and the coins in many countries. Mixed with zinc it makes brass, and with tin it makes bronze.',
        ],
      },
      {
        heading: 'How copper was discovered',
        paragraphs: [
          'People started using copper around 10,000 years ago, hammering natural lumps into beads and tools in the Middle East. Later they learned to melt copper out of rocks in fires. The Romans got much of their copper from the island of Cyprus, and its Latin name, cuprum, gives copper its symbol, Cu.',
        ],
      },
      {
        heading: 'Three fun facts about copper',
        paragraphs: [
          'Octopuses, squid and some snails have blue blood, because they use copper instead of iron to carry oxygen.',
          'Germs die quickly on copper surfaces, so copper is used on some door handles and hospital surfaces.',
          'The Statue of Liberty is covered in copper sheets that have turned green over time.',
        ],
      },
    ],
  },
  Zn: {
    intro: 'Zinc is a bluish-silver metal that protects steel from rust, helps make brass, and keeps your body healthy. Most people use something made with zinc every day without knowing it.',
    sections: [
      {
        heading: 'Where is zinc found?',
        paragraphs: [
          'Zinc is mined mainly from an ore called sphalerite, which is zinc sulfide, often found together with lead ores. China, Peru and Australia produce the most. Zinc is also found in foods such as meat, shellfish, beans, nuts and seeds.',
        ],
      },
      {
        heading: 'What do we use zinc for?',
        paragraphs: [
          "About half of all zinc is used to coat steel so it won't rust, which is called galvanising. Zinc is mixed with copper to make brass, and it is used in batteries and die-cast metal parts such as toy cars. Zinc oxide is the white cream in some sunscreens and nappy rash creams.",
        ],
      },
      {
        heading: 'How zinc was discovered',
        paragraphs: [
          'People in India were making pure zinc in furnaces at Zawar in Rajasthan hundreds of years before Europeans, by heating zinc ore and collecting the zinc vapour. In Europe, the German chemist Andreas Marggraf described how to make it in 1746. The name may come from an old German word meaning prong or point, after the shape of zinc crystals.',
        ],
      },
      {
        heading: 'Three fun facts about zinc',
        paragraphs: [
          'Your body needs zinc to fight germs, heal cuts, and taste and smell properly.',
          'Pennies made in the United States since 1982 are mostly zinc with a thin coat of copper.',
          'Zinc is soft and bendy when warmed to around 100 to 150 °C, but brittle at room temperature.',
        ],
      },
    ],
  },
  Br: {
    intro: 'Bromine is a dark red-brown liquid that gives off choking orange fumes. It is the only non-metal element that is a liquid at room temperature.',
    sections: [
      {
        heading: 'Where is bromine found?',
        paragraphs: [
          'Bromine is too reactive to be found pure in nature. It is found as bromide salts dissolved in sea water, and in much higher amounts in salty lakes and underground brines. The Dead Sea, between Israel and Jordan, and salt brines in the United States are major sources.',
        ],
      },
      {
        heading: 'What do we use bromine for?',
        paragraphs: [
          'Bromine compounds are added to some plastics, fabrics and electronics to make them harder to set on fire. Others are used to keep hot tubs and spas clean, to drill for oil and gas, and to make medicines and dyes. Silver bromide was once essential for photographic film.',
        ],
      },
      {
        heading: 'How bromine was discovered',
        paragraphs: [
          'In 1826 Antoine-Jérôme Balard, a young chemistry teacher in Montpellier, France, was studying the brine left over from making salt from sea water. He added chlorine and saw the liquid turn orange-brown. He separated a dark red liquid that turned out to be a new element. It was named after bromos, the Greek word for stench.',
        ],
      },
      {
        heading: 'Three fun facts about bromine',
        paragraphs: [
          'Bromine and mercury are the only two elements that are liquid at room temperature.',
          'The precious ancient dye Tyrian purple, worn by emperors, contains bromine.',
          'Bromine fumes are so heavy and strong-smelling that just a few drops can colour the air in a large jar orange.',
        ],
      },
    ],
  },
  Ag: {
    intro: 'Silver is a shiny white metal that has been treasured for thousands of years. It carries electricity better than any other element and reflects light beautifully.',
    sections: [
      {
        heading: 'Where is silver found?',
        paragraphs: [
          'Silver is sometimes found as pure metal in rocks, but most silver comes from ores of lead, copper and zinc, where it is mixed in small amounts. Mexico, Peru and China mine the most silver.',
        ],
      },
      {
        heading: 'What do we use silver for?',
        paragraphs: [
          'Silver is used for jewellery, cutlery, coins and medals. Because it carries electricity so well, it is used in electronics, switches and solar panels. Silver makes the shiny backing of good mirrors, and because it kills germs it is used in some bandages and medical equipment. Silver compounds were once the key to photographic film.',
        ],
      },
      {
        heading: 'How silver was discovered',
        paragraphs: [
          "Silver has been known since ancient times and was used for money and decoration by many early civilisations. In the 1500s, a mountain called Cerro Rico at Potosí in Bolivia became one of the richest silver mines in history. Silver's symbol, Ag, comes from argentum, its Latin name.",
        ],
      },
      {
        heading: 'Three fun facts about silver',
        paragraphs: [
          'Silver is the best conductor of both electricity and heat of all the elements.',
          'A compound called silver iodide is sprayed into clouds to try to help make rain or snow.',
          'Silver turns black when it reacts with sulfur in the air, which is why old silver needs polishing.',
        ],
      },
    ],
  },
  Sn: {
    intro: 'Tin is a soft, silvery metal that does not rust easily. Mixed with copper it made bronze, which changed human history thousands of years ago.',
    sections: [
      {
        heading: 'Where is tin found?',
        paragraphs: [
          'Tin is mined mainly from a dark mineral called cassiterite, which is tin oxide. It is often found as heavy grains in rivers and gravel. Today China, Indonesia and Myanmar produce the most tin. For centuries Cornwall in England was famous for its tin mines.',
        ],
      },
      {
        heading: 'What do we use tin for?',
        paragraphs: [
          'The biggest use of tin today is solder, a metal that melts easily and is used to join parts together in electronics. Tin is used to coat steel food cans, which is why we call them tin cans. It is also mixed into bronze, pewter and the metal used for organ pipes.',
        ],
      },
      {
        heading: 'How tin was discovered',
        paragraphs: [
          "Tin has been used for more than 5,000 years. Bronze Age people discovered that adding tin to copper made a much harder metal. Because tin ores were rare, they were traded over long distances, and Cornish tin may have been carried across Europe in ancient times. Tin's symbol, Sn, comes from its Latin name, stannum.",
        ],
      },
      {
        heading: 'Three fun facts about tin',
        paragraphs: [
          'When you bend a bar of tin, it makes a creaking sound called the tin cry.',
          'In very cold weather, tin can slowly crumble into grey powder, which is called tin pest.',
          'A tin can is mostly steel, with only a very thin coating of tin.',
        ],
      },
    ],
  },
  I: {
    intro: 'Iodine is a shiny, blue-black solid that turns into a beautiful violet gas when it is warmed. Your body needs a tiny amount of it to stay healthy.',
    sections: [
      {
        heading: 'Where is iodine found?',
        paragraphs: [
          'Iodine is found in sea water in very small amounts. Seaweed soaks it up, so seaweed is rich in iodine. Most iodine today comes from salty underground water in Japan and from mineral deposits in the deserts of Chile. Sea fish, eggs and dairy foods contain iodine too.',
        ],
      },
      {
        heading: 'What do we use iodine for?',
        paragraphs: [
          'Iodine is added to table salt so that people get enough in their diet. Iodine solutions are used to kill germs on skin before an operation. Iodine compounds help doctors see blood vessels on X-rays, and are used in medicines, animal feed and some dyes.',
        ],
      },
      {
        heading: 'How iodine was discovered',
        paragraphs: [
          'In 1811 the French chemist Bernard Courtois was making saltpetre for gunpowder from seaweed ash. When he added a strong acid, purple clouds rose and settled as dark, glittering crystals. Other chemists, including Joseph Louis Gay-Lussac, studied it and showed it was a new element. Its name comes from the Greek word for violet.',
        ],
      },
      {
        heading: 'Three fun facts about iodine',
        paragraphs: [
          'Iodine can change straight from a solid to a gas without melting first.',
          'A drop of iodine turns blue-black when it touches starch, which is a famous science test.',
          'Your thyroid gland, in your neck, uses iodine to make chemicals that control how fast your body works.',
        ],
      },
    ],
  },
  Pt: {
    intro: 'Platinum is a heavy, silvery-white precious metal that never tarnishes. It is rarer than gold and is one of the most useful metals for speeding up chemical reactions.',
    sections: [
      {
        heading: 'Where is platinum found?',
        paragraphs: [
          "Platinum is one of the rarest metals in Earth's crust. Most of it is mined in South Africa, from a huge area of ancient rock called the Bushveld, and in Russia. It is often found together with similar metals such as palladium, rhodium and iridium, and sometimes as small nuggets in river gravel.",
        ],
      },
      {
        heading: 'What do we use platinum for?',
        paragraphs: [
          'The biggest use of platinum is in catalytic converters, which clean the exhaust gases from cars. It is also used in jewellery, lab equipment, the tips of spark plugs, and in electrodes for heart pacemakers. A platinum compound called cisplatin is an important medicine for treating some cancers.',
        ],
      },
      {
        heading: 'How platinum was discovered',
        paragraphs: [
          'People in South America were working platinum into jewellery long before Europeans arrived. Spanish explorers first thought it was an annoying impurity in gold and called it platina, meaning little silver. The Spanish scientist Antonio de Ulloa wrote about it in 1748, and European scientists then studied it as a new metal.',
        ],
      },
      {
        heading: 'Three fun facts about platinum',
        paragraphs: [
          'From 1889 to 2019, the official kilogram was a small cylinder of platinum and iridium kept near Paris.',
          'Platinum does not rust or tarnish, even when heated in air.',
          'The official metre bar made in France in 1799 was also made of platinum.',
        ],
      },
    ],
  },
  Au: {
    intro: 'Gold is a soft, heavy, bright yellow metal that never rusts or tarnishes. For thousands of years people have treasured it for jewellery, art and money.',
    sections: [
      {
        heading: 'Where is gold found?',
        paragraphs: [
          "Gold is often found as pure metal: tiny flakes and grains in rocks, especially in quartz veins, and nuggets and dust in rivers, washed out of the rocks. China, Australia and Russia mine the most gold today. South Africa's Witwatersrand rocks have produced more gold than anywhere else in history.",
        ],
      },
      {
        heading: 'What do we use gold for?',
        paragraphs: [
          "About half of the gold mined each year becomes jewellery, and much of the rest is kept by banks and governments as a store of value. Gold is used in electronics, because it carries electricity well and never corrodes, and a thin layer of gold coats astronauts' visors to reflect sunlight.",
        ],
      },
      {
        heading: 'How gold was discovered',
        paragraphs: [
          "Gold has been known since prehistoric times, because it is found as shiny pure metal. Ancient Egyptians made beautiful gold jewellery and masks, including the famous mask of Tutankhamun. Gold's symbol, Au, comes from its Latin name, aurum.",
        ],
      },
      {
        heading: 'Three fun facts about gold',
        paragraphs: [
          'Gold can be hammered so thin that light shines through it as a green glow.',
          'All the gold ever mined would fit into a cube about 22 metres on each side.',
          'Gold is so unreactive that gold treasure found in shipwrecks still shines after hundreds of years.',
        ],
      },
    ],
  },
  Hg: {
    intro: 'Mercury is a shiny, silvery metal that is liquid at room temperature. It is beautiful to look at but poisonous, so it should never be touched.',
    sections: [
      {
        heading: 'Where is mercury found?',
        paragraphs: [
          'Mercury is rare. It is mostly found in a bright red mineral called cinnabar, which is mercury sulfide. Famous mercury mines include Almadén in Spain, which was mined for about 2,000 years. Small amounts of mercury are released naturally by volcanoes.',
        ],
      },
      {
        heading: 'What do we use mercury for?',
        paragraphs: [
          'Mercury used to be used in thermometers, barometers, dental fillings and many switches. Because it is so poisonous, most of these uses have been replaced by safer materials. Small amounts are still used in some lamps and scientific equipment, and countries have agreed to cut mercury use around the world.',
        ],
      },
      {
        heading: 'How mercury was discovered',
        paragraphs: [
          'Mercury has been known since ancient times. It has been found in Egyptian tombs more than 3,000 years old. Its old name, quicksilver, means living silver, because of the way its droplets move. The element is named after the speedy messenger god Mercury, and its symbol, Hg, comes from hydrargyrum, meaning water silver.',
        ],
      },
      {
        heading: 'Three fun facts about mercury',
        paragraphs: [
          'Mercury is the only metal that is liquid at room temperature.',
          'Mercury is so dense that an iron cannonball would float on it.',
          'Mercury freezes into a solid at about minus 39 °C.',
        ],
      },
    ],
  },
  Pb: {
    intro: 'Lead is a soft, heavy, bluish-grey metal. It is very useful but also poisonous, so today it is kept out of paint, petrol and water pipes.',
    sections: [
      {
        heading: 'Where is lead found?',
        paragraphs: [
          'Lead is mined mainly from a shiny grey ore called galena, which is lead sulfide and often forms neat cube-shaped crystals. China, Australia and Peru produce the most. Lead is also easy to recycle, and much of the lead used today comes from old car batteries.',
        ],
      },
      {
        heading: 'What do we use lead for?',
        paragraphs: [
          "Most lead is used in car batteries. Because it is so dense, lead blocks X-rays and other radiation, so it is used in the aprons and walls of X-ray rooms. It is also used on roofs, in weights and in some kinds of glass. Lead was once added to petrol and paint, but this was stopped because it harmed people's health.",
        ],
      },
      {
        heading: 'How lead was discovered',
        paragraphs: [
          'Lead has been used for thousands of years because it is easy to melt and shape. The Romans made water pipes out of lead, and the word plumber comes from plumbum, the Latin name for lead. That Latin name also gives lead its symbol, Pb.',
        ],
      },
      {
        heading: 'Three fun facts about lead',
        paragraphs: [
          'Pencil lead does not contain any lead. It is made of graphite, a form of carbon.',
          'Lead is so soft that you can scratch it with your fingernail.',
          'Lead is one of the best materials for blocking X-rays.',
        ],
      },
    ],
  },
  U: {
    intro: 'Uranium is a heavy, silvery metal that is naturally radioactive. Its atoms slowly break apart and give off energy, which can be used to make electricity in nuclear power stations.',
    sections: [
      {
        heading: 'Where is uranium found?',
        paragraphs: [
          'Uranium is found in small amounts in many rocks, soils and even sea water. It is mined from ores such as uraninite, once called pitchblende. Kazakhstan, Canada and Australia are among the biggest producers.',
        ],
      },
      {
        heading: 'What do we use uranium for?',
        paragraphs: [
          'Most uranium is used as fuel in nuclear power stations. When uranium atoms split, they release a great deal of heat, which is used to boil water and turn generators. Nuclear power makes electricity without burning coal or gas. Uranium is also used to power some submarines and large ships.',
        ],
      },
      {
        heading: 'How uranium was discovered',
        paragraphs: [
          'In 1789 the German chemist Martin Heinrich Klaproth found a new element in pitchblende and named it after the planet Uranus, discovered a few years before. In 1896 Henri Becquerel found that uranium salts gave off invisible rays that fogged photographic plates. Marie and Pierre Curie then studied pitchblende and discovered two more radioactive elements, polonium and radium.',
        ],
      },
      {
        heading: 'Three fun facts about uranium',
        paragraphs: [
          'Glass coloured with a little uranium glows bright green under ultraviolet light.',
          "Heat from uranium and other radioactive elements slowly breaking down helps keep Earth's inside hot.",
          'A small pellet of uranium fuel can produce as much energy as a tonne of coal.',
        ],
      },
    ],
  },
  Be: {
    intro: 'Beryllium is a light, steel-grey metal that is very stiff and strong for its weight. It is rare and its dust is harmful to breathe, so it is handled carefully in factories.',
    sections: [
      {
        heading: 'Where is beryllium found?',
        paragraphs: [
          'Beryllium is found in minerals such as beryl and bertrandite. Beryl is famous because its coloured crystals are precious gems: green emeralds and blue aquamarines are both kinds of beryl. Most beryllium is mined in the United States, especially in Utah.',
        ],
      },
      {
        heading: 'What do we use beryllium for?',
        paragraphs: [
          'Because it is light, stiff and keeps its shape when temperatures change, beryllium is used in spacecraft and satellites. X-rays pass through it easily, so it makes the windows of X-ray machines. Mixed with copper, it makes springy alloys for electrical parts and tools that do not make sparks.',
        ],
      },
      {
        heading: 'How beryllium was discovered',
        paragraphs: [
          'In 1798 the French chemist Louis-Nicolas Vauquelin found a new substance while studying beryl and emeralds. In 1828 Friedrich Wöhler in Germany and Antoine Bussy in France each managed to make the metal itself. The name comes from beryl, the mineral it was found in.',
        ],
      },
      {
        heading: 'Three fun facts about beryllium',
        paragraphs: [
          'The giant mirror segments of the James Webb Space Telescope are made of beryllium coated with a thin layer of gold.',
          'Emeralds get their green colour from tiny amounts of chromium inside the beryl crystal.',
          'Beryllium was once called glucinium, because some of its compounds taste sweet. They are poisonous, so nobody should ever taste them.',
        ],
      },
    ],
  },
  B: {
    intro: 'Boron is a hard, dark metalloid, partly like a metal and partly like a non-metal. You may already know one of its compounds: borax, which is used to make slime.',
    sections: [
      {
        heading: 'Where is boron found?',
        paragraphs: [
          "Boron is never found pure in nature. It is mined as borax and other boron minerals, mostly in Turkey, which has the world's biggest supplies, and in the dry desert lakes of California. Plants need tiny amounts of boron from the soil to grow properly.",
        ],
      },
      {
        heading: 'What do we use boron for?',
        paragraphs: [
          'Boron is added to glass to make borosilicate glass, which does not crack when it is heated, so it is used for oven dishes and laboratory glassware. Boron is used in fibreglass, in very hard materials for armour and cutting tools, in fertilisers and in some of the strongest magnets in the world.',
        ],
      },
      {
        heading: 'How boron was discovered',
        paragraphs: [
          'In 1808 the French chemists Joseph Louis Gay-Lussac and Louis Jacques Thénard, and Humphry Davy in England, each made impure boron at almost the same time. They heated boric acid with potassium. The name comes from borax, combined with the ending of carbon, because boron is a bit like carbon.',
        ],
      },
      {
        heading: 'Three fun facts about boron',
        paragraphs: [
          'In the 1880s, teams of 20 mules hauled huge wagons of borax out of Death Valley in California.',
          'Boron compounds make flames burn bright green.',
          'Boron atoms help make neodymium magnets, used in headphones and electric car motors.',
        ],
      },
    ],
  },
  Cr: {
    intro: 'Chromium is a hard, shiny, silvery metal that never seems to lose its sparkle. It is what makes stainless steel stainless and chrome bumpers gleam.',
    sections: [
      {
        heading: 'Where is chromium found?',
        paragraphs: [
          'Chromium is mined from a black mineral called chromite. South Africa produces far more than any other country, followed by Kazakhstan, Turkey and India. Tiny amounts of chromium are also found in foods such as broccoli, whole grains and meat.',
        ],
      },
      {
        heading: 'What do we use chromium for?',
        paragraphs: [
          'Most chromium is mixed into steel to make stainless steel, which needs at least about 10 percent chromium. Chromium forms an invisible protective layer on the surface that stops rust. A thin coat of chromium, called chrome plating, makes taps, car parts and bike handlebars shiny. Chromium compounds were once used in bright yellow, orange and green paints.',
        ],
      },
      {
        heading: 'How chromium was discovered',
        paragraphs: [
          'In 1797 the French chemist Louis-Nicolas Vauquelin studied a bright orange-red mineral from Siberia called crocoite and found a new metal inside it. Because its compounds come in so many bright colours, he named it after the Greek word chroma, meaning colour.',
        ],
      },
      {
        heading: 'Three fun facts about chromium',
        paragraphs: [
          'Rubies are red and emeralds are green because both contain small amounts of chromium.',
          'Stainless steel was invented in 1913 by Harry Brearley in Sheffield, England, while he was trying to make better gun barrels.',
          'Chromium is one of the hardest metals, which makes chrome plating tough to scratch.',
        ],
      },
    ],
  },
  Mn: {
    intro: 'Manganese is a hard, brittle, silvery-grey metal. You rarely hear about it, but it is in almost all steel, in many batteries and even in the enzymes inside your body.',
    sections: [
      {
        heading: 'Where is manganese found?',
        paragraphs: [
          'Manganese is mined mainly from a black mineral called pyrolusite, with South Africa, Gabon and Australia producing the most. Huge numbers of potato-sized lumps called manganese nodules also lie on the deep ocean floor. Nuts, whole grains, tea and leafy vegetables contain small amounts of manganese.',
        ],
      },
      {
        heading: 'What do we use manganese for?',
        paragraphs: [
          'About nine-tenths of all manganese goes into steel, where it removes unwanted sulfur and oxygen and makes the steel tougher. Drink cans contain a little manganese mixed with the aluminium. Manganese dioxide is a key ingredient in ordinary alkaline batteries.',
        ],
      },
      {
        heading: 'How manganese was discovered',
        paragraphs: [
          'Black manganese minerals were used as paint thousands of years ago. In 1774 the Swedish chemist Carl Wilhelm Scheele realised that the mineral pyrolusite contained a new element, and his friend Johan Gottlieb Gahn made the first manganese metal that same year by heating it with charcoal.',
        ],
      },
      {
        heading: 'Three fun facts about manganese',
        paragraphs: [
          'Some Stone Age cave painters used black manganese oxide powder in their art.',
          'A manganese compound called potassium permanganate makes a deep purple solution in water.',
          'Your body needs tiny amounts of manganese to help build bones and use the energy in food.',
        ],
      },
    ],
  },
  Co: {
    intro: 'Cobalt is a hard, silvery-blue metal that is magnetic like iron. It gives glass and pottery a beautiful deep blue colour, and it is an important part of many rechargeable batteries.',
    sections: [
      {
        heading: 'Where is cobalt found?',
        paragraphs: [
          "Cobalt is usually found together with copper and nickel ores. About seven-tenths of the world's cobalt comes from the Democratic Republic of the Congo in Africa. Small amounts are also mined in Indonesia, Russia and Australia.",
        ],
      },
      {
        heading: 'What do we use cobalt for?',
        paragraphs: [
          'Cobalt is used in the batteries of many phones, laptops and electric cars. It is added to special alloys for jet engines and gas turbines, which must stay strong when red hot. It is used in strong magnets, and cobalt compounds make the rich blue in glass, tiles and paints.',
        ],
      },
      {
        heading: 'How cobalt was discovered',
        paragraphs: [
          'Miners in Germany found ores that looked like they contained copper but gave off poisonous fumes when heated and produced no copper. They blamed mischievous goblins called kobolds. In about 1735 the Swedish chemist Georg Brandt showed that the blue colour came from a new metal, and it was named cobalt after the goblins.',
        ],
      },
      {
        heading: 'Three fun facts about cobalt',
        paragraphs: [
          'Vitamin B12, which your body needs to make red blood cells, has a cobalt atom at its centre.',
          'The famous blue and white porcelain made in China during the Ming dynasty was painted with cobalt blue.',
          'Iron, cobalt and nickel are the only three elements that are strongly magnetic at room temperature.',
        ],
      },
    ],
  },
  Ni: {
    intro: 'Nickel is a hard, silvery metal with a slight golden tinge. It resists rust, is magnetic, and is mixed into stainless steel, coins and batteries.',
    sections: [
      {
        heading: 'Where is nickel found?',
        paragraphs: [
          "Nickel is mined in Indonesia, the Philippines, Russia, Canada and Australia. One of the richest nickel areas is the Sudbury Basin in Canada, a huge crater made by a meteorite almost 2 billion years ago. Earth's core is thought to be made mostly of iron with some nickel, and many meteorites are iron-nickel alloys.",
        ],
      },
      {
        heading: 'What do we use nickel for?',
        paragraphs: [
          'Most nickel goes into stainless steel. It is also used in rechargeable batteries, including many electric car batteries, in coins, in plating that makes metal shiny and rust-proof, in guitar strings, and in heat-resistant alloys for jet engines.',
        ],
      },
      {
        heading: 'How nickel was discovered',
        paragraphs: [
          'German miners named a reddish ore kupfernickel, meaning something like copper demon, because it looked like copper ore but gave no copper. In 1751 the Swedish chemist Axel Fredrik Cronstedt discovered a new metal in it and called it nickel.',
        ],
      },
      {
        heading: 'Three fun facts about nickel',
        paragraphs: [
          'The American five-cent coin called a nickel is actually three-quarters copper and only one-quarter nickel.',
          'Nickel is magnetic, so fridge magnets stick to some nickel-plated objects.',
          'Some people get an itchy skin rash from jewellery containing nickel.',
        ],
      },
    ],
  },
  Ga: {
    intro: 'Gallium is a soft, silvery metal that melts at just under 30 °C. Hold a piece in your hand and it slowly turns into a shiny liquid.',
    sections: [
      {
        heading: 'Where is gallium found?',
        paragraphs: [
          "Gallium does not have ores of its own. It is found in tiny amounts in bauxite, the ore used to make aluminium, and in zinc ores. Almost all gallium is collected as a by-product when these metals are made, and China produces most of the world's supply.",
        ],
      },
      {
        heading: 'What do we use gallium for?',
        paragraphs: [
          "Gallium's biggest uses are in electronics. Gallium nitride is used to make blue and white LEDs and fast phone chargers. Gallium arsenide is used in chips for mobile phones and in high-efficiency solar panels for satellites. A liquid mix of gallium, indium and tin is used in some thermometers instead of mercury.",
        ],
      },
      {
        heading: 'How gallium was discovered',
        paragraphs: [
          'In 1871 Dmitri Mendeleev predicted an undiscovered element below aluminium in his periodic table. In 1875 the French chemist Paul-Émile Lecoq de Boisbaudran spotted two new violet lines when he studied light from a zinc ore. He had found the missing element and named it gallium, after Gallia, the Latin name for France.',
        ],
      },
      {
        heading: 'Three fun facts about gallium',
        paragraphs: [
          'Like water, gallium expands when it freezes, so it should not be stored in glass bottles.',
          'A spoon made of gallium melts when it is stirred into a cup of hot tea.',
          'Gallium stays liquid over a huge range of temperatures, from about 30 °C to over 2,000 °C.',
        ],
      },
    ],
  },
  Ge: {
    intro: 'Germanium is a shiny, grey-white metalloid that looks like a metal but is brittle like glass. It helped start the electronics age, because the very first transistor was made with it.',
    sections: [
      {
        heading: 'Where is germanium found?',
        paragraphs: [
          "Germanium is rare and spread thinly. It is mostly collected as a by-product from zinc ores and from the ash left after burning some kinds of coal. China produces most of the world's germanium.",
        ],
      },
      {
        heading: 'What do we use germanium for?',
        paragraphs: [
          'Germanium lets infrared light pass through it, so it is used to make lenses for night-vision and thermal cameras. Germanium compounds are added to the glass in fibre-optic cables that carry internet signals. It is also used in solar cells on satellites and in some electronic chips.',
        ],
      },
      {
        heading: 'How germanium was discovered',
        paragraphs: [
          "Mendeleev predicted an element he called eka-silicon in 1871. In 1886 the German chemist Clemens Winkler found it in a silver mineral called argyrodite. Its properties matched Mendeleev's predictions so well that they helped convince scientists the periodic table really worked. Winkler named it after his country, Germany.",
        ],
      },
      {
        heading: 'Three fun facts about germanium',
        paragraphs: [
          'In 1947 scientists at Bell Labs in the United States made the first working transistor using a crystal of germanium.',
          'Germanium looks shiny and solid but shatters if you drop it.',
          'Like water and gallium, germanium expands when it freezes.',
        ],
      },
    ],
  },
  As: {
    intro: 'Arsenic is a grey, brittle metalloid best known as a poison. Even so, it has some important uses in electronics, and scientists study it carefully because of its dangers.',
    sections: [
      {
        heading: 'Where is arsenic found?',
        paragraphs: [
          'Arsenic is found in many minerals, often together with sulfur, copper and lead. Two arsenic minerals are brightly coloured: red realgar and yellow orpiment. Arsenic can also seep naturally from rocks into underground water, which is a serious problem in some parts of the world, so drinking water is tested for it.',
        ],
      },
      {
        heading: 'What do we use arsenic for?',
        paragraphs: [
          'Arsenic is used in tiny, careful amounts to make gallium arsenide chips for phones and satellites, and to help harden lead. In the past it was used in pesticides, wood preservatives and some medicines, but most of those uses have stopped because arsenic is so toxic.',
        ],
      },
      {
        heading: 'How arsenic was discovered',
        paragraphs: [
          'Arsenic minerals have been known since ancient times, when orpiment was used as a yellow paint. The German scholar Albertus Magnus is often said to have made the element itself around the year 1250. Its name comes from an old Persian word for the yellow mineral orpiment.',
        ],
      },
      {
        heading: 'Three fun facts about arsenic',
        paragraphs: [
          "In the 1800s, a bright green wallpaper dye called Scheele's green contained arsenic, and it could make people ill.",
          'When arsenic is heated, it gives off a smell like garlic.',
          'Heated arsenic turns straight from a solid into a gas without melting.',
        ],
      },
    ],
  },
  Kr: {
    intro: 'Krypton is a colourless noble gas with no smell. It is so rare in the air that it was hidden from scientists until they learned to turn air into a liquid.',
    sections: [
      {
        heading: 'Where is krypton found?',
        paragraphs: [
          'Krypton makes up only about one part per million of the air. It is collected from liquid air in factories that separate out oxygen and nitrogen, and only small amounts are produced.',
        ],
      },
      {
        heading: 'What do we use krypton for?',
        paragraphs: [
          'Krypton is used in some energy-saving windows, where it fills the gap between the panes and slows the escape of heat. It is used in some bright lamps and photography flashes, and krypton fluoride lasers help make the tiny patterns on computer chips.',
        ],
      },
      {
        heading: 'How krypton was discovered',
        paragraphs: [
          'In 1898 the British chemists William Ramsay and Morris Travers let liquid air slowly boil away and studied what was left. They found a new gas giving off bright lines of light in their spectroscope. They named it krypton, from the Greek word kryptos, meaning hidden.',
        ],
      },
      {
        heading: 'Three fun facts about krypton',
        paragraphs: [
          'From 1960 to 1983 the official length of a metre was defined using the orange-red light given off by krypton atoms.',
          "Superman's kryptonite is a made-up mineral. Real krypton is a harmless, invisible gas.",
          'Krypton can form a few compounds with fluorine, but only under very special conditions.',
        ],
      },
    ],
  },
  Rb: {
    intro: 'Rubidium is a very soft, silvery-white alkali metal that melts at about 39 °C, not much warmer than a hot day. It reacts so strongly that it can catch fire in air and explodes in water.',
    sections: [
      {
        heading: 'Where is rubidium found?',
        paragraphs: [
          'Rubidium is spread thinly through many rocks but rarely forms minerals of its own. It is found in minerals such as lepidolite and pollucite, and is usually collected as a by-product of mining lithium and caesium.',
        ],
      },
      {
        heading: 'What do we use rubidium for?',
        paragraphs: [
          'Rubidium atoms tick very steadily, so they are used in compact atomic clocks, including clocks on GPS satellites and in phone networks. Scientists use rubidium in research on light and cold atoms, and rubidium compounds give a purple colour to some fireworks.',
        ],
      },
      {
        heading: 'How rubidium was discovered',
        paragraphs: [
          'In 1861 Robert Bunsen and Gustav Kirchhoff, who had found caesium a year earlier, studied the mineral lepidolite with their spectroscope. They saw two deep red lines that matched no known element. They named the element rubidium, from the Latin word rubidus, meaning deep red.',
        ],
      },
      {
        heading: 'Three fun facts about rubidium',
        paragraphs: [
          'In 1995 scientists cooled rubidium atoms to just above absolute zero and made a new state of matter, called a Bose-Einstein condensate. They later won a Nobel Prize for it.',
          'Rubidium would melt on a very hot summer day.',
          'Rubidium is so reactive that it is stored in sealed glass tubes, away from air.',
        ],
      },
    ],
  },
  Sr: {
    intro: 'Strontium is a soft, silvery alkaline earth metal that reacts quickly with air and water. Its compounds are famous for making the bright red colours in fireworks.',
    sections: [
      {
        heading: 'Where is strontium found?',
        paragraphs: [
          'Strontium is found mainly in two minerals, celestine and strontianite. Spain, China and Mexico are the biggest producers. Strontium is also found in sea water, and small amounts end up in bones and teeth, because the body treats it a bit like calcium.',
        ],
      },
      {
        heading: 'What do we use strontium for?',
        paragraphs: [
          'Strontium compounds make deep red fireworks and emergency road flares. Strontium aluminate is used in modern glow-in-the-dark paint and signs, because it glows much longer than older materials. Some toothpastes for sensitive teeth contain strontium compounds, and strontium was once used in the glass of old box-shaped TV screens.',
        ],
      },
      {
        heading: 'How strontium was discovered',
        paragraphs: [
          'In 1790 the Scottish doctor Adair Crawford studied a mineral from lead mines near the village of Strontian, in the Scottish Highlands, and realised it contained something new. In 1808 Humphry Davy used electricity to make the metal itself, and named it after the village.',
        ],
      },
      {
        heading: 'Three fun facts about strontium',
        paragraphs: [
          'Strontium is one of the few elements named after a village.',
          'Strontium makes a flame turn bright crimson red.',
          'Glow-in-the-dark exit signs made with strontium aluminate can glow for hours after the lights go out.',
        ],
      },
    ],
  },
  Zr: {
    intro: 'Zirconium is a strong, silvery-grey metal that does not rust and can stand up to very hot water. Its mineral, zircon, contains some of the oldest pieces of Earth ever found.',
    sections: [
      {
        heading: 'Where is zirconium found?',
        paragraphs: [
          'Zirconium is mined mainly as the mineral zircon, which is found as tiny grains in beach sands. Australia and South Africa produce the most. Zircon grains are so tough that they survive for billions of years as the rocks around them wear away.',
        ],
      },
      {
        heading: 'What do we use zirconium for?',
        paragraphs: [
          'Zirconium lets neutrons pass through easily and resists corrosion, so it is used for the tubes holding fuel in nuclear power stations. Zirconium oxide, called zirconia, makes very hard ceramic knives, dental crowns and artificial gems called cubic zirconia, which look like diamonds.',
        ],
      },
      {
        heading: 'How zirconium was discovered',
        paragraphs: [
          'In 1789 the German chemist Martin Heinrich Klaproth found a new element while studying zircon gems from Sri Lanka. In 1824 the Swedish chemist Jöns Jacob Berzelius made the first impure zirconium metal. The name comes from zircon, which may come from a Persian word for gold-coloured.',
        ],
      },
      {
        heading: 'Three fun facts about zirconium',
        paragraphs: [
          'Tiny zircon crystals from Western Australia are about 4.4 billion years old, among the oldest pieces of Earth known.',
          'Cubic zirconia is often used as a cheaper sparkling stone instead of diamond.',
          'Fine zirconium powder burns with a brilliant white light, and was once used in camera flashbulbs.',
        ],
      },
    ],
  },
  Mo: {
    intro: 'Molybdenum, pronounced mol-IB-den-um, is a hard, silvery metal that stays strong at very high temperatures. It is also an element that plants, animals and you need in tiny amounts.',
    sections: [
      {
        heading: 'Where is molybdenum found?',
        paragraphs: [
          'Molybdenum is mined mainly from the mineral molybdenite, which is molybdenum sulfide. China, Chile and the United States produce the most. Beans, lentils, grains and nuts contain small amounts.',
        ],
      },
      {
        heading: 'What do we use molybdenum for?',
        paragraphs: [
          'Most molybdenum is added to steel to make it stronger and harder, and better at resisting heat and corrosion. These steels are used for tools, pipelines, engines and stainless steel kitchen sinks. Molybdenum sulfide is a slippery black powder used as a lubricant, and molybdenum compounds help clean sulfur out of fuels.',
        ],
      },
      {
        heading: 'How molybdenum was discovered',
        paragraphs: [
          'For a long time people confused molybdenite with graphite and with lead ore, because they all look grey and soft. In 1778 Carl Wilhelm Scheele showed that molybdenite was something different. In 1781 his friend Peter Jacob Hjelm made the metal. The name comes from molybdos, the Greek word for lead.',
        ],
      },
      {
        heading: 'Three fun facts about molybdenum',
        paragraphs: [
          'Bacteria that turn nitrogen from the air into plant food use molybdenum to do it.',
          'Molybdenum melts at about 2,620 °C, more than twice as hot as copper.',
          'Molybdenum sulfide powder is so slippery that it is used to grease machines that get too hot for oil.',
        ],
      },
    ],
  },
  Pd: {
    intro: 'Palladium is a shiny, silvery-white precious metal that never tarnishes. It is famous for soaking up hydrogen gas and for helping chemical reactions happen faster.',
    sections: [
      {
        heading: 'Where is palladium found?',
        paragraphs: [
          "Palladium is rare and is usually found together with platinum and nickel. Russia and South Africa produce most of the world's palladium, from mines in Siberia and in the Bushveld area of South Africa.",
        ],
      },
      {
        heading: 'What do we use palladium for?',
        paragraphs: [
          'Most palladium goes into catalytic converters, which clean harmful gases from car exhausts. It is used in electronics, in dental fillings and crowns, and in white-gold jewellery. Thin sheets of palladium are used as filters to make very pure hydrogen, because only hydrogen can pass through.',
        ],
      },
      {
        heading: 'How palladium was discovered',
        paragraphs: [
          'In 1802 the English chemist William Hyde Wollaston discovered palladium while purifying platinum ore. He named it after the asteroid Pallas, which had just been discovered. He then put it on sale in a London shop without saying who had found it, which made some chemists suspect it was a fake.',
        ],
      },
      {
        heading: 'Three fun facts about palladium',
        paragraphs: [
          'Palladium can soak up about 900 times its own volume of hydrogen gas.',
          'In 2010 three chemists won the Nobel Prize for using palladium to join carbon atoms together, a method now used to make many medicines.',
          'Far less palladium is mined each year than gold.',
        ],
      },
    ],
  },
  Cd: {
    intro: 'Cadmium is a soft, bluish-white metal that is poisonous. It once had many uses, but today it is used much less and is handled carefully to keep it out of the environment.',
    sections: [
      {
        heading: 'Where is cadmium found?',
        paragraphs: [
          'Cadmium is not mined for itself. It is found in small amounts in zinc ores and is collected when zinc is made. Cadmium can build up in soil and water near old mines and factories, which is why it is carefully monitored.',
        ],
      },
      {
        heading: 'What do we use cadmium for?',
        paragraphs: [
          'Cadmium was widely used in rechargeable nickel-cadmium batteries, which are now being replaced. Cadmium compounds make brilliant yellow, orange and red paints for artists. Cadmium telluride is used in some thin solar panels, and cadmium rods help control nuclear reactors because cadmium soaks up neutrons.',
        ],
      },
      {
        heading: 'How cadmium was discovered',
        paragraphs: [
          'In 1817 the German chemist Friedrich Stromeyer was checking zinc compounds used in medicine. One sample turned yellow when heated, which pure zinc compounds should not do. He traced the colour to a new metal and named it cadmium, after cadmia, an old Latin name for zinc ore.',
        ],
      },
      {
        heading: 'Three fun facts about cadmium',
        paragraphs: [
          'Like tin, cadmium makes a crackling sound when a bar of it is bent.',
          'Bright cadmium yellow and red paints have been used by artists since the 1800s.',
          'Cadmium pollution from a mine in Japan caused a painful bone disease, which led to stricter rules about mining waste.',
        ],
      },
    ],
  },
  Xe: {
    intro: 'Xenon is a heavy, colourless noble gas. It was the first noble gas ever made to form a compound, and it glows a bright bluish-white in lamps.',
    sections: [
      {
        heading: 'Where is xenon found?',
        paragraphs: [
          'Xenon is one of the rarest gases in the air, less than one part in ten million. It is collected from liquid air in factories that separate oxygen and nitrogen, so it is expensive.',
        ],
      },
      {
        heading: 'What do we use xenon for?',
        paragraphs: [
          'Xenon makes the very bright light in some car headlights, cinema projectors and camera flashes. It powers ion engines on spacecraft, which push gently but for years. Doctors can use xenon as a gas to put patients to sleep for operations, and to take special scans of the lungs.',
        ],
      },
      {
        heading: 'How xenon was discovered',
        paragraphs: [
          'In 1898 William Ramsay and Morris Travers found xenon in the last traces left after liquid air had almost completely boiled away. They named it after the Greek word xenos, meaning stranger. In 1962 Neil Bartlett made the first compound of a noble gas using xenon.',
        ],
      },
      {
        heading: 'Three fun facts about xenon',
        paragraphs: [
          'Xenon lamps are used in large cinema projectors because their light is very bright and close in colour to sunlight.',
          "NASA's Dawn spacecraft used xenon ion engines to travel to the asteroid Vesta and the dwarf planet Ceres.",
          'Xenon is about four and a half times heavier than air.',
        ],
      },
    ],
  },
  Cs: {
    intro: 'Caesium is a soft, pale gold metal and one of the most reactive elements. It melts at about 28 °C and explodes on touching water, so it is kept sealed in glass.',
    sections: [
      {
        heading: 'Where is caesium found?',
        paragraphs: [
          'Caesium is rare. Most of it comes from a mineral called pollucite, and one of the main sources has been a mine at Bernic Lake in Manitoba, Canada. It is also found in small amounts in some mineral springs and rocks.',
        ],
      },
      {
        heading: 'What do we use caesium for?',
        paragraphs: [
          'Caesium atomic clocks are the most important clocks in the world, because they define the official length of a second. Caesium formate, a very dense liquid, helps drill deep oil and gas wells. Caesium is also used in some devices that turn light into electricity.',
        ],
      },
      {
        heading: 'How caesium was discovered',
        paragraphs: [
          'In 1860 Robert Bunsen and Gustav Kirchhoff evaporated a large amount of mineral water and studied the leftover salts with their new spectroscope. They saw two bright blue lines nobody had seen before and named the element after the Latin word caesius, meaning sky blue. It was the first element found using a spectroscope.',
        ],
      },
      {
        heading: 'Three fun facts about caesium',
        paragraphs: [
          'Caesium, copper and gold are the only metals that are not silvery-grey in colour.',
          'Caesium would melt on a warm summer day.',
          'In American English it is spelled cesium.',
        ],
      },
    ],
  },
  Ba: {
    intro: 'Barium is a soft, silvery metal that reacts with air and water. Its name comes from the Greek word for heavy, because its minerals feel surprisingly weighty.',
    sections: [
      {
        heading: 'Where is barium found?',
        paragraphs: [
          'Barium is mined mostly as the mineral baryte, also called barite, which is barium sulfate. China, India and Morocco are among the main producers. Baryte is often found in veins with lead and zinc ores.',
        ],
      },
      {
        heading: 'What do we use barium for?',
        paragraphs: [
          'Most baryte is ground up and mixed into the heavy mud used when drilling oil and gas wells. Barium sulfate is also given to patients as a white drink called a barium meal, which shows up their stomach and intestines clearly on X-rays. Barium compounds make green fireworks.',
        ],
      },
      {
        heading: 'How barium was discovered',
        paragraphs: [
          'In the early 1600s, an Italian shoemaker named Vincenzo Casciarolo heated a stone near Bologna and found it glowed in the dark. That stone was baryte. In 1774 Carl Wilhelm Scheele recognised a new element in it, and in 1808 Humphry Davy made the metal using electricity.',
        ],
      },
      {
        heading: 'Three fun facts about barium',
        paragraphs: [
          'Barium sulfate is safe to drink for X-rays because it does not dissolve, but other barium compounds are poisonous.',
          'Barium makes a flame turn green.',
          'The glowing Bologna stone was one of the first glow-in-the-dark materials ever studied.',
        ],
      },
    ],
  },
  Nd: {
    intro: 'Neodymium, pronounced nee-oh-DIM-ee-um, is a silvery metal from the group called the rare earths. Mixed with iron and boron, it makes the strongest permanent magnets in the world.',
    sections: [
      {
        heading: 'Where is neodymium found?',
        paragraphs: [
          "Despite the name rare earth, neodymium is more common in Earth's crust than lead. It is found mixed with other rare earth elements in minerals such as monazite and bastnäsite. China mines and processes most of the world's supply.",
        ],
      },
      {
        heading: 'What do we use neodymium for?',
        paragraphs: [
          'Neodymium magnets are small but very powerful. They are inside headphones, earbuds, phone speakers, computer hard drives, electric car motors and wind turbines. Neodymium is also used in powerful lasers and to colour glass purple, and in special glasses that glassblowers wear.',
        ],
      },
      {
        heading: 'How neodymium was discovered',
        paragraphs: [
          'In 1885 the Austrian chemist Carl Auer von Welsbach showed that a substance called didymium, thought to be a single element, was really two. He named them praseodymium and neodymium. Neodymium means new twin.',
        ],
      },
      {
        heading: 'Three fun facts about neodymium',
        paragraphs: [
          'Neodymium magnets are so strong that two of them can pinch fingers painfully if they snap together.',
          'Electric cars and wind turbines each need large amounts of neodymium for their motors and generators.',
          'Neodymium makes glass look lilac in daylight and blue under some lamps.',
        ],
      },
    ],
  },
  W: {
    intro: 'Tungsten is a very heavy, steel-grey metal that has the highest melting point of any metal. It glowed inside old light bulbs without melting.',
    sections: [
      {
        heading: 'Where is tungsten found?',
        paragraphs: [
          "Tungsten is mined from the minerals wolframite and scheelite. China produces most of the world's tungsten, with smaller amounts from Vietnam, Russia and Bolivia.",
        ],
      },
      {
        heading: 'What do we use tungsten for?',
        paragraphs: [
          'Most tungsten is made into tungsten carbide, an extremely hard material used for drill bits, cutting tools and the tips of mining equipment. Tungsten was used for the glowing wires in old light bulbs. It is also used in X-ray tubes, welding, heavy weights for racing cars and darts, and some jewellery.',
        ],
      },
      {
        heading: 'How tungsten was discovered',
        paragraphs: [
          'In 1781 Carl Wilhelm Scheele found a new acid in the mineral now called scheelite. In 1783 two Spanish brothers, Juan José and Fausto Elhuyar, made the metal for the first time from wolframite. The name tungsten comes from Swedish words meaning heavy stone, and its symbol, W, comes from wolfram, its other name.',
        ],
      },
      {
        heading: 'Three fun facts about tungsten',
        paragraphs: [
          'Tungsten melts at about 3,420 °C, higher than any other metal.',
          'Tungsten is almost exactly as dense as gold, so fake gold bars have sometimes been made with tungsten hidden inside.',
          'The thin glowing wire in an old light bulb was made of tungsten.',
        ],
      },
    ],
  },
  Bi: {
    intro: 'Bismuth is a brittle, pinkish-silver metal that makes some of the most beautiful crystals in chemistry. When it cools, it forms rainbow-coloured staircase shapes.',
    sections: [
      {
        heading: 'Where is bismuth found?',
        paragraphs: [
          "Bismuth is rare and is usually collected as a by-product when lead, copper and tin are refined. China produces most of the world's bismuth.",
        ],
      },
      {
        heading: 'What do we use bismuth for?',
        paragraphs: [
          'Bismuth is used in a well-known pink stomach medicine, and in some make-up to give a pearly shine. Because it is much less toxic than lead, it is used instead of lead in solder, fishing weights and shot. Bismuth alloys that melt at low temperatures are used in fire sprinklers, which open when they get hot.',
        ],
      },
      {
        heading: 'How bismuth was discovered',
        paragraphs: [
          'Bismuth was known by the 1400s, but people often confused it with lead and tin. In 1753 the French chemist Claude François Geoffroy showed that it was a separate element. Its name probably comes from an old German phrase about white metal.',
        ],
      },
      {
        heading: 'Three fun facts about bismuth',
        paragraphs: [
          'The rainbow colours on bismuth crystals come from a very thin layer of oxide on their surface.',
          'Bismuth expands when it freezes, just like water.',
          'Bismuth is very slightly radioactive, but it decays so slowly that its atoms last over a billion times longer than the age of the universe.',
        ],
      },
    ],
  },
  Rn: {
    intro: 'Radon is a colourless, odourless and radioactive noble gas. You cannot see or smell it, but it can seep out of the ground, so many homes are tested for it.',
    sections: [
      {
        heading: 'Where is radon found?',
        paragraphs: [
          'Radon is made naturally when uranium in rocks and soil slowly breaks down. It seeps up through the ground into the air. Outdoors it spreads out harmlessly, but it can build up in basements and ground floors, especially in areas with granite rocks.',
        ],
      },
      {
        heading: 'What do we use radon for?',
        paragraphs: [
          'Radon has few uses today. It was once used in some cancer treatments. Scientists measure radon levels in soil and water to study the ground. Most importantly, people use radon testing kits to check their homes and can add ventilation if levels are high.',
        ],
      },
      {
        heading: 'How radon was discovered',
        paragraphs: [
          'Radon was discovered around 1899 and 1900, when scientists, including Ernest Rutherford and the German physicist Friedrich Ernst Dorn, noticed that radioactive elements such as radium and thorium gave off a radioactive gas. It was later named radon, after radium.',
        ],
      },
      {
        heading: 'Three fun facts about radon',
        paragraphs: [
          'Radon is the heaviest gas found naturally on Earth.',
          'Radon breaks down quickly: after about four days, half of a sample has turned into other elements.',
          'Breathing high levels of radon for many years can harm the lungs, which is why homes in some areas are tested.',
        ],
      },
    ],
  },
  Ra: {
    intro: 'Radium is a rare, radioactive metal that glows faintly in the dark. It made Marie and Pierre Curie famous, and taught the world about the dangers of radiation.',
    sections: [
      {
        heading: 'Where is radium found?',
        paragraphs: [
          'Radium is found in very tiny amounts in uranium ores, where it forms as uranium slowly breaks down. There is so little that the Curies had to process tonnes of ore to collect a tenth of a gram of a radium compound.',
        ],
      },
      {
        heading: 'What do we use radium for?',
        paragraphs: [
          'In the early 1900s, radium paint was used to make watch and clock numbers glow in the dark, until people learned how dangerous it was. Today a form of radium is used, very carefully, as a medicine to treat some cancers that have spread to bones.',
        ],
      },
      {
        heading: 'How radium was discovered',
        paragraphs: [
          'In 1898 Marie and Pierre Curie discovered radium while studying pitchblende, a uranium ore that was more radioactive than its uranium could explain. They named it after the Latin word radius, meaning ray. Marie Curie later won two Nobel Prizes, one in physics and one in chemistry.',
        ],
      },
      {
        heading: 'Three fun facts about radium',
        paragraphs: [
          "Marie Curie's notebooks are still radioactive and are kept in lead-lined boxes.",
          'Pure radium compounds give off a faint blue glow.',
          'Workers who painted glowing watch dials with radium in the 1920s became ill, and their court case led to better safety laws for workers.',
        ],
      },
    ],
  },
  Pu: {
    intro: 'Plutonium is a heavy, silvery, radioactive metal. Almost all plutonium on Earth is made by people inside nuclear reactors.',
    sections: [
      {
        heading: 'Where is plutonium found?',
        paragraphs: [
          'Only the tiniest traces of plutonium exist naturally, in some uranium ores. Nearly all of it is produced in nuclear reactors, where uranium atoms soak up extra neutrons and change into plutonium. It is stored and handled in special secure facilities.',
        ],
      },
      {
        heading: 'What do we use plutonium for?',
        paragraphs: [
          'Plutonium can be used as fuel in some nuclear power stations. One kind, plutonium-238, gives off steady heat for decades, so it powers spacecraft that travel too far from the Sun to use solar panels, and rovers on Mars such as Curiosity and Perseverance.',
        ],
      },
      {
        heading: 'How plutonium was discovered',
        paragraphs: [
          'Plutonium was first made in 1940 and 1941 by a team led by Glenn Seaborg at the University of California, Berkeley. Uranium had been named after the planet Uranus, and neptunium after Neptune, so the next element was named after Pluto.',
        ],
      },
      {
        heading: 'Three fun facts about plutonium',
        paragraphs: [
          'The Voyager space probes, launched in 1977, are still sending messages back to Earth using power from plutonium-238.',
          'A lump of plutonium-238 feels warm, and can even glow red, from its own heat.',
          'Glenn Seaborg later had an element named after him, seaborgium, while he was still alive.',
        ],
      },
    ],
  },
});

export function findElementArticle(symbol) {
  return ELEMENT_ARTICLES[symbol] ?? null;
}
